import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { type WorkOrder } from '@/types';
import { Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';

interface CancelWorkOrderDialogProps {
    workOrder: WorkOrder;
    isOpen: boolean;
    onClose: () => void;
    onCancel?: (reason: string) => void;
}

export function CancelWorkOrderDialog({ isOpen, onClose, onCancel }: CancelWorkOrderDialogProps) {
    const { t } = useTranslations();
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!reason.trim()) {
            return;
        }

        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            onCancel?.(reason.trim());
            setIsSubmitting(false);
            setReason('');
            onClose();
        }, 500);
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setReason('');
            onClose();
        }
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle className="flex items-center gap-2">
                        <XCircle className="text-destructive h-5 w-5" />
                        {t('work_orders.dialogs.cancel.title', 'Cancel Work Order')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'work_orders.dialogs.cancel.description',
                            'Please provide a reason for cancelling this work order. This information will be visible to all team members.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="reason">{t('work_orders.dialogs.cancel.cancellation_reason', 'Cancellation Reason *')}</Label>
                            <Textarea
                                id="reason"
                                placeholder={t(
                                    'work_orders.dialogs.cancel.placeholder_reason',
                                    'e.g., Customer requested cancellation, Equipment unavailable, etc.',
                                )}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={isSubmitting}
                                rows={4}
                                maxLength={500}
                                required
                            />
                            <p className="text-muted-foreground text-right text-xs">
                                {reason.length}/500 {t('work_orders.dialogs.cancel.characters', 'characters')}
                            </p>
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            {t('work_orders.dialogs.cancel.keep_active', 'Keep Active')}
                        </Button>
                        <Button type="submit" variant="destructive" disabled={isSubmitting || !reason.trim()} className="w-full sm:w-auto">
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('work_orders.dialogs.cancel.cancelling', 'Cancelling...')}
                                </>
                            ) : (
                                <>
                                    <XCircle className="mr-2 h-4 w-4" />
                                    {t('work_orders.dialogs.cancel.cancel_work_order', 'Cancel Work Order')}
                                </>
                            )}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
